/**
 * Listener mixin for objects that subscribe to publishers (Actions/Stores).
 * Used by Stores to listen to Actions and other Stores.
 *
 * @module reflux/listener
 */

import { capitalize, callbackName } from './utils.js';
import { ACTION_BUILT_IN_PROPERTIES } from './constants.js';

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Flattens listenables object to include async action children.
 * Recursively processes action children to create flattened key-value pairs.
 *
 * @param {Object} listenables - Object with action names as keys, actions as values
 * @returns {Object} Flattened object including children with camelCase names
 *
 * @example
 * // Input:
 * {
 *   login: UserActions.login // has .completed and .failed children
 * }
 *
 * // Output:
 * {
 *   login: UserActions.login,
 *   loginCompleted: UserActions.login.completed,
 *   loginFailed: UserActions.login.failed
 * }
 */
const flattenListenables = (listenables) => {
  const flattened = {};

  for (const key in listenables) {
    const listenable = listenables[key];
    if (!listenable) continue;

    // Add the primary listenable
    flattened[key] = listenable;

    // Check for children (async actions) - look for properties that are listenables
    // Actions can be functions or objects, so check for .listen method
    for (const childKey in listenable) {
      const child = listenable[childKey];

      // Skip if not defined or not a listenable
      if (!child || typeof child.listen !== 'function') {
        continue;
      }

      // Skip known non-child properties (these are methods/properties on the action itself)
      if (ACTION_BUILT_IN_PROPERTIES.has(childKey)) {
        continue;
      }

      // This looks like a child action (e.g., 'completed', 'failed', 'complete', 'fail')
      // Create camelCase name: 'login' + 'Completed' → 'loginCompleted'
      const flattenedKey = key + capitalize(childKey);
      flattened[flattenedKey] = child;
    }
  }

  return flattened;
};

/**
 * Validates that listening to a listenable is safe.
 * Prevents self-listening, invalid listenables, and circular dependencies.
 *
 * @param {Object} listener - The listening object (store)
 * @param {Object} listenable - The object being listened to (action/store)
 * @returns {string|undefined} Error message or undefined if valid
 *
 * @example
 * const error = validateListening(storeA, storeB);
 * if (error) {
 *   throw new Error(error);
 * }
 */
export const validateListening = (listener, listenable) => {
  if (listenable === listener) {
    return "Listener is not able to listen to itself";
  }

  if (!listenable || typeof listenable.listen !== 'function') {
    return `${listenable} is missing a listen method`;
  }

  if (listenable.hasListener?.(listener)) {
    return "Listener cannot listen to this listenable because of circular loop";
  }

  return undefined;
};

/**
 * Checks if listener has a subscription to a specific listenable.
 * Recursively checks for indirect subscriptions to detect circular dependencies.
 *
 * @param {Object} listener - The listening object
 * @param {Object} listenable - The listenable to search for
 * @returns {boolean} True if listener has subscription (direct or indirect)
 *
 * @example
 * if (hasListener(storeA, storeB)) {
 *   console.log('StoreA is listening to StoreB');
 * }
 */
const hasListener = (listener, listenable) => {
  const subs = listener.subscriptions || [];

  return subs.some(sub => {
    // Handle both single and multiple listenables using flat()
    const listenables = [sub.listenable].flat();

    return listenables.some(l =>
      // Direct match or recursive check for indirect subscriptions
      l === listenable || l.hasListener?.(listenable)
    );
  });
};

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Fetches initial state from a publisher if it has getInitialState method.
 * Supports both synchronous and Promise-based initial state.
 *
 * @param {Object} listener - The listening object (store/component)
 * @param {Object} listenable - The publisher (action/store)
 * @param {Function|string} defaultCallback - Callback to receive initial state
 *
 * @example
 * // Synchronous initial state
 * fetchInitialState(component, UserStore, 'onUserState');
 *
 * @example
 * // Promise-based initial state
 * store.getInitialState = () => {
 *   return fetchUserData().then(data => data);
 * };
 * fetchInitialState(component, store, (data) => console.log(data));
 */
const fetchInitialState = (listener, listenable, defaultCallback) => {
  // Resolve callback: if string, look up on listener; if function, use directly
  const callback = typeof defaultCallback === 'string'
    ? listener[defaultCallback]
    : defaultCallback;

  if (typeof callback === 'function' && typeof listenable.getInitialState === 'function') {
    const data = listenable.getInitialState();

    // Handle Promise-based initial state
    if (data && typeof data.then === 'function') {
      data.then((...args) => {
        callback.apply(listener, args);
      });
    } else {
      // Synchronous initial state
      callback.call(listener, data);
    }
  }
};

// ============================================================================
// LISTENER METHODS
// ============================================================================

/**
 * Creates listener methods for a store or component.
 * Provides listenTo, stopListeningTo, stopListeningToAll, and hasListener methods.
 *
 * @param {Object} listener - The object that will listen (store/component)
 * @returns {Object} Listener methods
 *
 * @example
 * const store = {
 *   init() {
 *     Object.assign(this, createListenerMethods(this));
 *     this.listenTo(UserActions.login, this.onLogin);
 *   },
 *   onLogin(credentials) {
 *     console.log('Login triggered:', credentials);
 *   }
 * };
 */
export const createListenerMethods = (listener) => {
  // Ensure subscriptions array exists
  listener.subscriptions = listener.subscriptions || [];

  return {
    /**
     * Check if this listener has a subscription to a listenable.
     * Recursively checks for indirect subscriptions.
     *
     * @param {Object} listenable - The listenable to search for
     * @returns {boolean} True if subscribed (directly or indirectly)
     *
     * @example
     * if (this.hasListener(UserActions.login)) {
     *   console.log('Already listening to login action');
     * }
     */
    hasListener(listenable) {
      return hasListener(listener, listenable);
    },

    /**
     * Listen to a publisher (action or store).
     * Automatically fetches initial state if available.
     * Returns subscription object with stop() method for cleanup.
     *
     * @param {Object} listenable - Action or Store to listen to
     * @param {Function|string} callback - Callback function or method name
     * @param {Function|string} [defaultCallback] - Callback for initial state
     * @returns {Object} Subscription object with stop() method and listenable reference
     * @throws {Error} If validation fails (self-listening, circular deps, invalid listenable)
     *
     * @example
     * // In store init()
     * this.listenTo(UserActions.login, this.onLogin, this.onLoginDefault);
     *
     * @example
     * // With string method name
     * this.listenTo(SomeStore, 'handleChange');
     *
     * @example
     * // With cleanup
     * const sub = this.listenTo(SomeStore, (state) => {
     *   console.log('State:', state);
     * });
     * // Later...
     * sub.stop();
     */
    listenTo(listenable, callback, defaultCallback) {
      // Validate listening is safe
      const error = validateListening(listener, listenable);
      if (error) {
        throw new Error(error);
      }

      // Fetch initial state if available (before listening to avoid race)
      fetchInitialState(listener, listenable, defaultCallback);

      // Resolve callback to actual function
      const resolvedCallback = listener[callback] || callback;

      // Subscribe to listenable
      const desub = listenable.listen(resolvedCallback, listener);

      // Create subscription object (will be populated below)
      const subscriptionobj = {
        stop: null, // Set after unsubscriber is created
        listenable: listenable
      };

      // Create unsubscriber
      const unsubscriber = () => {
        const index = listener.subscriptions.indexOf(subscriptionobj);

        if (index === -1) {
          throw new Error("Tried to remove listen already gone from subscriptions list!");
        }

        listener.subscriptions.splice(index, 1);
        desub();
      };

      subscriptionobj.stop = unsubscriber;
      listener.subscriptions.push(subscriptionobj);

      return subscriptionobj;
    },

    /**
     * Listen to multiple listenables at once.
     * Automatically generates callback method names from listenable keys.
     * Handles async action children automatically (e.g., loginCompleted, loginFailed).
     *
     * @param {Object} listenables - Object where keys are action names, values are actions/stores
     *
     * @example
     * // In store init()
     * this.listenToMany({
     *   login: UserActions.login,        // → calls this.onLogin()
     *   loadSites: UserActions.loadSites // → calls this.onLoadSites()
     * });
     *
     * @example
     * // If UserActions.login has .completed and .failed children:
     * // Also automatically binds:
     * //   UserActions.login.completed → this.onLoginCompleted()
     * //   UserActions.login.failed → this.onLoginFailed()
     *
     * @example
     * // DomainSettingsStore pattern (from real codebase)
     * this.listenToMany({
     *   loadRedirects: DomainActions.loadRedirects,
     *   deleteRedirect: DomainActions.deleteRedirect,
     *   addRedirect: DomainActions.addRedirect
     * });
     * // Automatically binds: onLoadRedirects, onLoadRedirectsComplete, onLoadRedirectsFail, etc.
     */
    listenToMany(listenables) {
      // Flatten to include async action children
      const allListenables = flattenListenables(listenables);

      // For each listenable, find matching callback and listen
      for (const key in allListenables) {
        const listenable = allListenables[key];

        // Generate callback name: 'login' → 'onLogin'
        const cbname = callbackName(key);

        // Check which callback name exists on this listener
        // Priority: 1) onActionName (preferred), 2) actionName (fallback), 3) skip
        const localname = listener[cbname] ? cbname : listener[key] ? key : undefined;

        if (localname) {
          // Find default callback for getInitialState
          // Try: onActionNameDefault, actionNameDefault, or use main callback
          const defaultCb =
            listener[cbname + 'Default'] ||
            listener[localname + 'Default'] ||
            localname;

          // Listen using listenTo (already defined above)
          this.listenTo(listenable, localname, defaultCb);
        }
        // If no callback exists, silently skip (matches legacy Reflux behavior)
      }
    },

    /**
     * Stop listening to a specific listenable.
     * Removes the subscription and calls the unlisten function.
     *
     * @param {Object} listenable - The listenable to stop listening to
     * @returns {boolean} True if subscription was found and removed
     * @throws {Error} If subscription removal validation fails
     *
     * @example
     * this.stopListeningTo(UserActions.login);
     *
     * @example
     * // Check if subscription existed
     * const wasListening = this.stopListeningTo(SomeStore);
     * if (!wasListening) {
     *   console.log('Was not listening to that store');
     * }
     */
    stopListeningTo(listenable) {
      const subs = listener.subscriptions || [];

      for (let i = 0; i < subs.length; i++) {
        const sub = subs[i];
        if (sub.listenable === listenable) {
          sub.stop();

          // Verify removal (matches Reflux behavior)
          if (subs.indexOf(sub) !== -1) {
            throw new Error("Failed to remove listen from subscriptions list!");
          }

          return true;
        }
      }

      return false;
    },

    /**
     * Stop all subscriptions.
     * Calls stop() on each subscription and validates cleanup.
     *
     * @throws {Error} If any subscription removal validation fails
     *
     * @example
     * // In store cleanup/destroy
     * this.stopListeningToAll();
     *
     * @example
     * // In React component useEffect cleanup
     * useEffect(() => {
     *   store.listenTo(UserStore, handleUpdate);
     *   return () => store.stopListeningToAll();
     * }, []);
     */
    stopListeningToAll() {
      const subs = listener.subscriptions || [];

      while (subs.length > 0) {
        const remaining = subs.length;
        subs[0].stop();

        // Verify removal (matches Reflux behavior)
        if (subs.length !== remaining - 1) {
          throw new Error("Failed to remove listen from subscriptions list!");
        }
      }
    }
  };
};
